import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconLoader2, IconPrinter, IconX } from '@tabler/icons-react';
import {
  CHECK_IN_TERMS_WAIVER_REASONS,
  CHECK_IN_TERMS_WAIVER_REASON_MAX_LENGTH,
  type CheckInContinueWithoutDigitalAcceptanceRequest,
  type CheckInPrintTermsRequest,
  type CheckInTermsWaiverReasonCode,
} from '@techgitter/contracts';

import type { CheckInPrintTermsResult } from '../../../../services/checkInApi';
import './checkInDialog.css';

type PrintTarget = CheckInPrintTermsRequest['target'];

type PrintState =
  | { status: 'idle' }
  | { status: 'printing' }
  | { status: 'sent' }
  | { status: 'skipped' }
  | { status: 'failed'; message: string };

const TARGETS: { target: PrintTarget; label: string; lane: string }[] = [
  { target: 'receipt', label: 'Print to receipt printer', lane: 'receipt printer' },
  { target: 'a4', label: 'Print to A4 printer', lane: 'A4 printer' },
];

export interface ContinueWithoutDigitalAcceptanceDialogProps {
  open: boolean;
  onClose: () => void;
  onPrint: (target: PrintTarget) => Promise<CheckInPrintTermsResult>;
  onConfirm: (payload: CheckInContinueWithoutDigitalAcceptanceRequest) => Promise<void>;
}

/**
 * Continue a check-in when the customer can't accept the terms on their own
 * device: print the terms, hand them over, tick that you did, continue.
 *
 * The tick is what the API enforces (`handedOver: true`) — printing is the
 * means, not a gate, so a printer that is down can't strand a customer at the
 * counter. Continuing never records an acceptance; the check-in records that
 * printed terms were handed over.
 */
export const ContinueWithoutDigitalAcceptanceDialog: React.FC<ContinueWithoutDigitalAcceptanceDialogProps> = ({
  open,
  onClose,
  onPrint,
  onConfirm,
}) => {
  const [reasonCode, setReasonCode] = useState<CheckInTermsWaiverReasonCode | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [handedOver, setHandedOver] = useState(false);
  const [prints, setPrints] = useState<Record<PrintTarget, PrintState>>({
    receipt: { status: 'idle' },
    a4: { status: 'idle' },
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setReasonCode(null);
    setReasonText('');
    setHandedOver(false);
    setPrints({ receipt: { status: 'idle' }, a4: { status: 'idle' } });
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  if (!open) return null;

  const needsText = reasonCode === 'other';
  const reasonReady = !!reasonCode && (!needsText || reasonText.trim().length > 0);
  const canContinue = reasonReady && handedOver && !submitting;

  const print = async (target: PrintTarget) => {
    setPrints(p => ({ ...p, [target]: { status: 'printing' } }));
    try {
      const result = await onPrint(target);
      setPrints(p => ({ ...p, [target]: result.published ? { status: 'sent' } : { status: 'skipped' } }));
    } catch (e) {
      setPrints(p => ({
        ...p,
        [target]: { status: 'failed', message: e instanceof Error ? e.message : 'Print failed' },
      }));
    }
  };

  const handleSubmit = async () => {
    if (!canContinue || !reasonCode) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({
        reasonCode,
        reasonText: reasonText.trim() || null,
        handedOver: true,
      });
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to continue the check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) handleClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog" role="dialog" aria-modal="true" aria-label="Continue without digital acceptance">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Continue without digital acceptance</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body">
          <div className="ci-dialog__desc">
            Print the terms and give them to the customer, then continue. The check-in records
            that printed terms were handed over — not that the customer accepted them.
          </div>

          <span className="ci-dialog__field-label">Why can&apos;t the customer accept digitally?</span>
          <div className="ci-dialog__options" role="radiogroup" aria-label="Reason">
            {CHECK_IN_TERMS_WAIVER_REASONS.map(({ code, label }) => {
              const selected = reasonCode === code;
              return (
                <label
                  key={code}
                  className={'ci-dialog__option' + (selected ? ' ci-dialog__option--selected' : '')}
                >
                  <input
                    type="radio"
                    name="terms-waiver-reason"
                    value={code}
                    checked={selected}
                    disabled={submitting}
                    onChange={() => {
                      setReasonCode(code);
                      if (error) setError(null);
                    }}
                  />
                  <span className="ci-dialog__option-text">
                    <span className="ci-dialog__option-title">{label}</span>
                  </span>
                </label>
              );
            })}
          </div>
          {needsText && (
            <textarea
              className="ci-dialog__textarea"
              aria-label="Reason"
              placeholder="Enter the reason"
              maxLength={CHECK_IN_TERMS_WAIVER_REASON_MAX_LENGTH}
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              disabled={submitting}
            />
          )}

          <span className="ci-dialog__field-label">Print the terms</span>
          <div className="ci-dialog__print-row">
            {TARGETS.map(({ target, label }) => (
              <button
                key={target}
                type="button"
                className="ci-dialog__print-btn"
                onClick={() => print(target)}
                disabled={submitting || prints[target].status === 'printing'}
              >
                {prints[target].status === 'printing'
                  ? <IconLoader2 size={15} stroke={1.5} className="ci-dialog__spin" />
                  : <IconPrinter size={15} stroke={1.5} />}
                {label}
              </button>
            ))}
          </div>
          {TARGETS.map(({ target, lane }) => {
            const state = prints[target];
            if (state.status === 'sent') {
              return (
                <div key={target} className="ci-dialog__print-status ci-dialog__print-status--ok">
                  Sent to the {lane}.
                </div>
              );
            }
            if (state.status === 'skipped') {
              return (
                <div key={target} className="ci-dialog__print-status ci-dialog__print-status--warn">
                  No {lane} is set up — nothing was printed.
                </div>
              );
            }
            if (state.status === 'failed') {
              return (
                <div key={target} className="ci-dialog__print-status ci-dialog__print-status--warn">
                  {state.message}
                </div>
              );
            }
            return null;
          })}

          <label className="ci-dialog__check">
            <input
              type="checkbox"
              checked={handedOver}
              onChange={e => setHandedOver(e.target.checked)}
              disabled={submitting}
            />
            I have given the printed Terms and Conditions to the customer
          </label>

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button type="button" className="ci-dialog__cancel" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--primary"
            onClick={handleSubmit}
            disabled={!canContinue}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Continuing…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
